import React, { useState, useCallback, useRef } from 'react';
import { Search } from 'lucide-react';
import {
  Input,
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@roger/ui-components';
import { searchDiagnosisCodesRouteDiagnosisCodesSearchGet } from '../../services/api/sdk.gen';
import { useDebounce } from '../../hooks/useDebounce';
import type {
  DiagnosisCodeSearchResult,
  PrimaryDiagnosisFitness,
} from '../../services/api/types.gen';
import { fitnessPresentation } from '../../utils/primaryDiagnosisFitness';
import { getBusinessErrorMessage } from '../../utils/apiErrorDetail';

const SEARCH_FAILED_MESSAGE = 'Unable to search codes. Please try again.';

interface InlineDiagnosisSearchProps {
  description?: string;
  onSelect: (
    code: string,
    description: string,
    fitness?: PrimaryDiagnosisFitness
  ) => void;
  isGhost?: boolean;
  placeholder?: string;
  disabled?: boolean;
  excludeCodes?: string[];
  /**
   * Whether a code chosen here lands in the primary seat.
   *
   * The CMS acceptability flags bar a code from the *principal* position and
   * carry no meaning for a secondary, so marking results outside the primary
   * context would be a false statement about the data — and with 41% of
   * billable codes unfit, noise that trains coders to stop reading. This
   * follows the precedent the ghost row's placeholder already sets, which
   * changes with the same condition.
   */
  primaryContext?: boolean;
  /** False softens the mark for payers that do not group through PDGM. */
  pdgmRelevant?: boolean;
  /**
   * The record's date (YYYY-MM-DD). The search offers the codes valid on that
   * date — a September visit offers I42.0, an October one I42.00. Omitted
   * means today's codes.
   */
  asOf?: string;
  /**
   * The From date (YYYY-MM-DD) of the claim period being edited, with `asOf`
   * its Through date. Each result's primary fitness is then judged as the
   * CMS grouper judges that claim. Requires `asOf`.
   */
  claimFromDate?: string;
}

const highlightMatch = (text: string, query: string): React.ReactNode => {
  if (!query || query.length < 2) return text;
  const lowerText = text.toLowerCase();
  const lowerQuery = query.toLowerCase();
  const index = lowerText.indexOf(lowerQuery);
  if (index === -1) return text;
  return (
    <>
      {text.slice(0, index)}
      <span className='font-bold'>
        {text.slice(index, index + query.length)}
      </span>
      {text.slice(index + query.length)}
    </>
  );
};

const InlineDiagnosisSearch: React.FC<InlineDiagnosisSearchProps> = ({
  description,
  onSelect,
  isGhost = false,
  placeholder = 'Search diagnosis code...',
  disabled = false,
  excludeCodes,
  primaryContext = false,
  pdgmRelevant = true,
  asOf,
  claimFromDate,
}) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [results, setResults] = useState<DiagnosisCodeSearchResult[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  // A refused search (e.g. no code set is loaded for the record's date) must
  // not read as "no codes match".
  const [searchError, setSearchError] = useState<string | null>(null);
  const [open, setOpen] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const excludeCodesRef = useRef(excludeCodes);
  excludeCodesRef.current = excludeCodes;
  // Read at search time, like excludeCodes, so the debounced search always
  // uses the date the caller holds now.
  const asOfRef = useRef(asOf);
  asOfRef.current = asOf;
  const claimFromDateRef = useRef(claimFromDate);
  claimFromDateRef.current = claimFromDate;

  const displayValue = isEditing ? searchTerm : description || '';

  const performSearch = useCallback(async (term: string) => {
    if (!term || term.length < 2) {
      setResults([]);
      setSearchError(null);
      setIsLoading(false);
      return;
    }
    setIsLoading(true);
    setSearchError(null);
    try {
      const response = await searchDiagnosisCodesRouteDiagnosisCodesSearchGet({
        query: {
          search_text: term,
          as_of: asOfRef.current || null,
          claim_from_date: claimFromDateRef.current || null,
        },
      });
      if (response.error) {
        console.error('Error searching diagnosis codes:', response.error);
        setResults([]);
        setSearchError(
          getBusinessErrorMessage(response) ?? SEARCH_FAILED_MESSAGE
        );
        return;
      }
      const data = response.data ?? [];
      const codes = excludeCodesRef.current;
      setResults(codes ? data.filter(d => !codes.includes(d.name)) : data);
    } catch (error) {
      console.error('Error searching diagnosis codes:', error);
      setResults([]);
      setSearchError(SEARCH_FAILED_MESSAGE);
    } finally {
      setIsLoading(false);
    }
  }, []);

  const debouncedSearch = useDebounce(performSearch, 300);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setSearchTerm(value);
    setIsEditing(true);

    if (value.length >= 2) {
      setOpen(true);
      setIsLoading(true);
      debouncedSearch(value);
    } else {
      setResults([]);
      setOpen(false);
    }
  };

  const handleFocus = () => {
    setIsEditing(true);
    setSearchTerm(description || '');
    if ((description || '').length >= 2) {
      setOpen(true);
      debouncedSearch(description || '');
    }
  };

  const handleSelect = (dx: DiagnosisCodeSearchResult) => {
    // Hand the fitness back with the code so the caller can gate its confirm
    // without a second round-trip for something we already know.
    onSelect(dx.name, dx.desc, dx.primary_fitness);
    setSearchTerm('');
    setResults([]);
    setOpen(false);
    setIsEditing(false);
    inputRef.current?.blur();
  };

  const handleBlur = (e: React.FocusEvent) => {
    // If focus moved to the popover content, don't close
    const relatedTarget = e.relatedTarget as HTMLElement | null;
    if (relatedTarget?.closest('[data-radix-popper-content-wrapper]')) return;
    setIsEditing(false);
    setSearchTerm('');
    setOpen(false);
  };

  const showDropdown = open && isEditing && searchTerm.length >= 2;

  return (
    <Popover open={showDropdown} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <div
          className={`relative flex-1 min-w-0 ${isGhost ? 'opacity-50' : ''}`}
        >
          <Search className='absolute left-2 top-1/2 w-3.5 -translate-y-1/2 text-muted-foreground z-10' />
          <Input
            ref={inputRef}
            type='text'
            placeholder={placeholder}
            value={displayValue}
            onChange={handleInputChange}
            onFocus={handleFocus}
            onBlur={handleBlur}
            className='pl-7 pr-7 text-sm'
            disabled={disabled}
          />
        </div>
      </PopoverTrigger>
      <PopoverContent
        className='p-0 w-[var(--radix-popover-trigger-width)]'
        align='start'
        sideOffset={4}
        onOpenAutoFocus={e => e.preventDefault()}
      >
        {isLoading ? (
          <div className='px-3 py-4 text-center text-sm text-muted-foreground'>
            Searching...
          </div>
        ) : searchError ? (
          <div
            className='px-3 py-4 text-center text-sm text-destructive'
            data-testid='diagnosis-search-error'
          >
            {searchError}
          </div>
        ) : results.length > 0 ? (
          <ul className='max-h-[250px] overflow-auto py-1'>
            {results.map((dx, i) => {
              // Only in the primary seat, and only then: the flags say nothing
              // about a secondary. Every result stays selectable — this
              // discourages a choice, it does not lock the door.
              const mark = primaryContext
                ? fitnessPresentation(dx.primary_fitness, {
                    softened: !pdgmRelevant,
                  })
                : null;
              return (
                <li key={`${dx.name}-${i}`}>
                  <button
                    type='button'
                    className='w-full px-3 py-2 text-left hover:bg-muted focus:bg-muted focus:outline-none'
                    onMouseDown={e => e.preventDefault()}
                    onClick={() => handleSelect(dx)}
                  >
                    {/* Wraps rather than squeezing: the popover is only as wide
                        as the search field, which is full-width on a phone, and
                        the mark must not eat the description. No effect where
                        the row already fits. */}
                    <div className='flex items-start gap-2 flex-wrap'>
                      <span className='font-mono text-xs font-semibold text-primary mt-0.5 shrink-0'>
                        {highlightMatch(dx.name, searchTerm)}
                      </span>
                      <span className='text-sm truncate'>
                        {highlightMatch(dx.desc, searchTerm)}
                      </span>
                      {mark && (
                        <span
                          className={`text-[10px] uppercase tracking-wide mt-0.5 shrink-0 whitespace-nowrap ${mark.markClass}`}
                          title={mark.message}
                        >
                          {mark.label}
                        </span>
                      )}
                    </div>
                  </button>
                </li>
              );
            })}
          </ul>
        ) : (
          <div className='px-3 py-4 text-center text-sm text-muted-foreground'>
            No diagnosis codes found for &quot;{searchTerm}&quot;
          </div>
        )}
      </PopoverContent>
    </Popover>
  );
};

export default InlineDiagnosisSearch;
